import React, { useState, useRef, useEffect } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from '@ui';
import './OverflowActionsMenu.scss';
function OverflowActionsMenu({ children, disabled = false, className = '' }) {
    const [overflowItems, setOverflowItems] = useState([]);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const itemsRef = useRef([]);
    const anchor = useRef(null);
    useEffect(() => {
        const checkOverflow = () => {
            if (!containerRef.current) {
                return;
            }
            const container = containerRef.current;
            const items = Array.from(itemsRef.current).filter(Boolean);
            if (items.length === 0) {
                return;
            }
            const containerWidth = container.offsetWidth;
            if (containerWidth === 0)
                return;
            const moreButtonWidth = anchor.current ? anchor.current.offsetWidth : 90;
            const gapWidth = 8;
            let totalItemsWidth = 0;
            const itemWidths = [];
            items.forEach((item) => {
                const width = item.offsetWidth;
                itemWidths.push(width);
                totalItemsWidth += width;
            });
            // Skip if items haven't been laid out yet
            if (items.some((item) => item.offsetWidth === 0)) {
                return;
            }
            totalItemsWidth += (items.length - 1) * gapWidth;
            // All items fit
            if (totalItemsWidth <= containerWidth - 20) {
                setOverflowItems([]);
                return;
            }
            // Calculate which items to hide
            const availableWidth = containerWidth - moreButtonWidth - gapWidth;
            let currentWidth = 0;
            let lastVisibleIndex = -1;
            for (let i = 0; i < items.length; i += 1) {
                const itemWidth = items[i].offsetWidth;
                const widthWithGap = currentWidth === 0 ? itemWidth : currentWidth + gapWidth + itemWidth;
                if (widthWithGap <= availableWidth) {
                    currentWidth = widthWithGap;
                    lastVisibleIndex = i;
                }
                else {
                    break;
                }
            }
            const overflow = [];
            for (let i = lastVisibleIndex + 1; i < items.length; i += 1) {
                overflow.push(i);
            }
            setOverflowItems(overflow);
        };
        window.addEventListener('resize', checkOverflow);
        const timeoutIds = [];
        const rafId = requestAnimationFrame(() => {
            timeoutIds.push(setTimeout(checkOverflow, 0));
        });
        timeoutIds.push(setTimeout(checkOverflow, 50));
        timeoutIds.push(setTimeout(checkOverflow, 100));
        timeoutIds.push(setTimeout(checkOverflow, 250));
        timeoutIds.push(setTimeout(checkOverflow, 500));
        return () => {
            window.removeEventListener('resize', checkOverflow);
            cancelAnimationFrame(rafId);
            timeoutIds.forEach((id) => clearTimeout(id));
        };
    }, [children]);
    const handleClose = () => {
        setOpen(false);
    };
    const handleTriggerClick = (e) => {
        e.stopPropagation();
        setOpen(!open);
    };
    const handlePopupClick = (e) => {
        e.stopPropagation();
    };
    const childArray = React.Children.toArray(children);
    return (React.createElement("div", { className: `overflow-actions-menu ${className}`, ref: containerRef },
        React.createElement("div", { className: "overflow-actions-menu__visible" }, childArray.map((child, index) => (React.createElement("div", { key: index, ref: (el) => {
                itemsRef.current[index] = el;
            }, className: "overflow-actions-menu__item", style: {
                display: 'block',
                visibility: overflowItems.includes(index) ? 'hidden' : 'visible',
                position: overflowItems.includes(index) ? 'absolute' : 'relative'
            } }, child)))),
        overflowItems.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(Button, { ref: anchor, onClick: handleTriggerClick, className: "overflow-actions-menu__trigger", disabled: disabled, title: "More actions", type: "button" },
                React.createElement("i", { className: "fa-light fa-ellipsis" }),
                "More"),
            React.createElement(Popup, { anchor: anchor.current, show: open, onClose: handleClose },
                React.createElement(ClickAwayListener, { onClickAway: handleClose },
                    React.createElement("div", { className: "overflow-actions-menu__popup", onClick: handlePopupClick, role: "dialog", "aria-label": "More actions menu" },
                        React.createElement("ul", { role: "presentation", className: "overflow-actions-menu__menu" }, overflowItems.map((itemIndex) => {
                            const child = childArray[itemIndex];
                            return (React.createElement("li", { key: itemIndex, role: "presentation", className: "overflow-actions-menu__menu-item", onClick: handleClose }, child));
                        })))))))));
}
export default OverflowActionsMenu;
